<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-ambulance me-2"></i>{{ $t('page.ambulance', 'Ambulance Service') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
          <button v-if="tab==='dispatch'" class="hms-btn hms-btn-red" @click="openDispatchForm()">
            <i class="mdi mdi-phone-in-talk me-1"></i>New Dispatch (911)
          </button>
          <button v-if="tab==='fleet'" class="hms-btn hms-btn-primary" @click="openVehicleForm()">
            <i class="mdi mdi-truck-plus me-1"></i>Add Ambulance
          </button>
          <button v-if="tab==='crew'" class="hms-btn hms-btn-primary" @click="openCrewForm()">
            <i class="mdi mdi-account-plus me-1"></i>Add Crew
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row mb-3">
        <div class="hms-stat"><div class="hms-stat-num">{{ vehicles.length }}</div><div class="hms-stat-lbl">Fleet</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-green">{{ statusCount('available') }}</div><div class="hms-stat-lbl">Available</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-blue">{{ enRouteCount }}</div><div class="hms-stat-lbl">En route / On scene</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-amber">{{ statusCount('out_of_service') + statusCount('maintenance') }}</div><div class="hms-stat-lbl">Out of service</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ crew.length }}</div><div class="hms-stat-lbl">Crew</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-green">{{ crew.filter(c => c.on_duty == 1).length }}</div><div class="hms-stat-lbl">On duty</div></div>
      </div>

      <!-- Tabs -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab==='map' }" @click="tab='map'; loadLive()">
          <i class="mdi mdi-map-marker-radius me-1"></i>Live Map
        </button>
        <button class="hms-tab" :class="{ active: tab==='dispatch' }" @click="tab='dispatch'; loadDispatches()">
          <i class="mdi mdi-headset me-1"></i>Dispatch Console
          <span v-if="activeDispatchCount" class="hms-tab-pill">{{ activeDispatchCount }}</span>
        </button>
        <button class="hms-tab" :class="{ active: tab==='fleet' }" @click="tab='fleet'">
          <i class="mdi mdi-truck me-1"></i>Fleet
        </button>
        <button class="hms-tab" :class="{ active: tab==='crew' }" @click="tab='crew'">
          <i class="mdi mdi-account-group me-1"></i>Crew
        </button>
        <button class="hms-tab" :class="{ active: tab==='reports' }" @click="tab='reports'; loadReports()">
          <i class="mdi mdi-chart-line me-1"></i>Reports
        </button>
      </div>

      <!-- ============ LIVE MAP ============ -->
      <div v-if="tab === 'map'" class="hms-card">
        <div class="hms-section-label d-flex justify-content-between align-items-center">
          <span>LIVE FLEET POSITIONS</span>
          <span class="hms-muted-sm">Auto-refreshes every 30s · {{ liveLocations.length }} vehicles</span>
        </div>
        <div ref="liveMap" class="hms-live-map mt-2"></div>
        <div v-if="liveLocations.length" class="row g-2 mt-3">
          <div v-for="v in liveLocations" :key="v.vehicle_id" class="col-md-4">
            <div class="hms-vehicle-pill" :class="'st-' + v.status" @click="focusVehicle(v)">
              <div class="hms-vp-head">
                <span class="hms-vp-sign">{{ v.call_sign || v.registration_no }}</span>
                <span class="hms-badge" :class="statusBadge(v.status)">{{ formatStatus(v.status) }}</span>
              </div>
              <div class="hms-muted-sm">
                <span v-if="v.latitude && v.longitude">
                  <i class="mdi mdi-map-marker"></i> {{ Number(v.latitude).toFixed(3) }}, {{ Number(v.longitude).toFixed(3) }}
                </span>
                <span v-else><i class="mdi mdi-map-marker-off"></i> No GPS</span>
                <span v-if="v.speed_kmh != null"> · {{ Math.round(v.speed_kmh) }} km/h</span>
              </div>
              <div class="hms-muted-sm">
                <span v-if="v.last_ping_at">Last ping {{ ago(v.seconds_since_ping) }}</span>
                <span v-else>No data</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-map-marker-off"></i>No vehicles reporting yet. Send a driver to the tracker URL (Fleet tab → copy tracker link).</div>
      </div>

      <!-- ============ FLEET ============ -->
      <div v-if="tab === 'fleet'" class="hms-card">
        <div v-if="vehicles.length" class="row g-3">
          <div v-for="v in vehicles" :key="v.vehicle_id" class="col-md-6">
            <div class="hms-vehicle-card">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div class="hms-vc-sign">{{ v.call_sign || '—' }}</div>
                  <div class="hms-mono">{{ v.registration_no }}</div>
                </div>
                <span class="hms-badge" :class="statusBadge(v.status)">{{ formatStatus(v.status) }}</span>
              </div>
              <div class="hms-muted-sm mb-2">
                {{ v.make }} {{ v.model }} <span v-if="v.year_of_manufacture">({{ v.year_of_manufacture }})</span>
                · {{ v.vehicle_type }}
                · {{ v.capacity_stretchers }} stretcher{{ v.capacity_stretchers === 1 ? '' : 's' }}
              </div>
              <div class="row g-1 mb-2">
                <div class="col-6 hms-muted-sm"><i class="mdi mdi-account-multiple me-1"></i>{{ v.crew_on_duty }} crew on duty</div>
                <div class="col-6 hms-muted-sm" v-if="v.fuel_level_pct != null"><i class="mdi mdi-fuel me-1"></i>{{ v.fuel_level_pct }}% fuel</div>
                <div class="col-6 hms-muted-sm" v-if="v.odometer_km"><i class="mdi mdi-counter me-1"></i>{{ Number(v.odometer_km).toLocaleString() }} km</div>
                <div class="col-6 hms-muted-sm" v-if="v.last_ping_at"><i class="mdi mdi-broadcast me-1"></i>{{ shortDateTime(v.last_ping_at) }}</div>
              </div>
              <div v-if="v.tracker_url" class="hms-tracker-line">
                <div class="hms-muted-sm mb-1"><i class="mdi mdi-cellphone-link me-1"></i>Driver tracker URL:</div>
                <div class="d-flex gap-1 align-items-center">
                  <code class="hms-mono hms-trk-url" :title="v.tracker_url">{{ v.tracker_url }}</code>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="copy(v.tracker_url)" title="Copy"><i class="mdi mdi-content-copy"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-blue" @click="smsTracker(v)" title="SMS to driver"><i class="mdi mdi-cellphone-message"></i></button>
                </div>
              </div>
              <div class="d-flex gap-2 mt-2">
                <button class="hms-btn hms-btn-sm hms-btn-amber" @click="openVehicleForm(v)"><i class="mdi mdi-pencil"></i> Edit</button>
                <button class="hms-btn hms-btn-sm hms-btn-blue"  @click="cycleStatus(v)" title="Change status"><i class="mdi mdi-swap-horizontal"></i> Status</button>
                <button class="hms-btn hms-btn-sm hms-btn-red"   @click="confirmDeleteVehicle(v)"><i class="mdi mdi-trash-can-outline"></i></button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-truck-outline"></i>No ambulances yet. Click <strong>Add Ambulance</strong> to start the fleet.</div>
      </div>

      <!-- ============ CREW ============ -->
      <div v-if="tab === 'crew'" class="hms-card">
        <div v-if="crew.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead>
              <tr>
                <th>Name</th><th>Role</th><th>Phone</th><th>Cert expiry</th><th>Assigned</th><th>Duty</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in crew" :key="c.crew_id">
                <td>
                  <div class="hms-text fw-semibold">{{ c.full_name }}</div>
                  <div class="hms-muted-sm" v-if="c.cert_number">{{ c.cert_number }}</div>
                </td>
                <td><span class="hms-badge hms-badge-info">{{ c.role }}</span></td>
                <td>{{ c.phone || '—' }}</td>
                <td>{{ c.cert_expiry_date || '—' }}</td>
                <td>
                  <span v-if="c.assigned_vehicle_callsign">{{ c.assigned_vehicle_callsign }}</span>
                  <span v-else-if="c.assigned_vehicle_reg" class="hms-mono">{{ c.assigned_vehicle_reg }}</span>
                  <span v-else class="hms-muted-sm">—</span>
                </td>
                <td>
                  <button class="hms-btn hms-btn-sm" :class="c.on_duty == 1 ? 'hms-btn-primary' : 'hms-btn-secondary'" @click="toggleDuty(c)">
                    {{ c.on_duty == 1 ? '🟢 On' : '○ Off' }}
                  </button>
                </td>
                <td>
                  <button class="hms-btn hms-btn-sm hms-btn-amber me-1" @click="openCrewForm(c)"><i class="mdi mdi-pencil"></i></button>
                  <button class="hms-btn hms-btn-sm hms-btn-red"  @click="confirmDeleteCrew(c)"><i class="mdi mdi-trash-can-outline"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-account-group-outline"></i>No crew yet. Click <strong>Add Crew</strong>.</div>
      </div>

      <!-- ============ DISPATCH CONSOLE ============ -->
      <div v-if="tab === 'dispatch'" class="hms-card">
        <div class="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <select class="hms-input hms-filter" v-model="dispatchStatusFilter" @change="loadDispatches">
            <option value="active">Active only</option>
            <option value="all">All</option>
            <option value="returned">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span class="hms-muted ms-auto">{{ dispatches.length }} dispatches</span>
        </div>
        <div v-if="dispatches.length" class="row g-3">
          <div v-for="d in dispatches" :key="d.dispatch_id" class="col-md-6 col-lg-4">
            <div class="hms-dispatch-card" :class="'pr-' + d.priority + ' st-' + d.status">
              <div class="hms-dc-head">
                <span class="hms-dc-no">{{ d.dispatch_no }}</span>
                <span class="hms-badge" :class="priorityBadge(d.priority)">{{ d.priority }}</span>
                <span class="hms-badge" :class="dispatchStatusBadge(d.status)">{{ formatDispatchStatus(d.status) }}</span>
              </div>
              <div class="hms-dc-body">
                <div class="hms-text fw-semibold mb-1">{{ d.complaint || 'No complaint noted' }}</div>
                <div class="hms-muted-sm" v-if="d.patient_full_name || d.patient_name_freetext">
                  <i class="mdi mdi-account"></i> {{ d.patient_full_name || d.patient_name_freetext }}
                  <span v-if="d.patient_age">· {{ d.patient_age }}y</span>
                </div>
                <div class="hms-muted-sm" v-if="d.pickup_location_name"><i class="mdi mdi-map-marker"></i> {{ d.pickup_location_name }}</div>
                <div class="hms-muted-sm" v-if="d.source_facility || d.dest_facility">
                  <i class="mdi mdi-transit-connection-variant"></i>
                  <span v-if="d.source_facility">{{ d.source_facility }}</span>
                  <span v-if="d.source_facility && d.dest_facility"> → </span>
                  <span v-if="d.dest_facility">{{ d.dest_facility }}</span>
                </div>
                <div class="hms-muted-sm" v-if="d.call_sign"><i class="mdi mdi-ambulance"></i> {{ d.call_sign }} ({{ d.registration_no }})</div>
                <div class="hms-muted-sm" v-if="d.caller_phone"><i class="mdi mdi-phone"></i> {{ d.caller_name || 'Caller' }} · {{ d.caller_phone }}</div>
                <div class="hms-muted-sm"><i class="mdi mdi-clock-outline"></i> {{ shortDateTime(d.call_received_at) }}</div>
              </div>
              <div class="hms-dc-actions">
                <button v-if="!d.vehicle_id && d.status === 'received'" class="hms-btn hms-btn-sm hms-btn-primary" @click="openAssign(d)">
                  <i class="mdi mdi-truck-fast"></i> Assign
                </button>
                <select v-if="d.status !== 'cancelled' && d.status !== 'returned'" class="hms-input hms-input-sm" :value="d.status" @change="transition(d, $event.target.value)" title="Change status">
                  <option v-for="s in dispatchStates" :key="s" :value="s">{{ formatDispatchStatus(s) }}</option>
                </select>
                <button v-if="d.caller_gps_url" class="hms-btn hms-btn-sm hms-btn-blue" @click="copy(d.caller_gps_url)" title="Caller GPS link">
                  <i class="mdi mdi-map-marker-plus"></i>
                </button>
                <button v-if="d.track_url" class="hms-btn hms-btn-sm hms-btn-blue" :href="d.track_url" @click.prevent="window.open(d.track_url, '_blank')" title="Live track">
                  <i class="mdi mdi-broadcast"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-phone-hangup"></i>No dispatches. Click <strong>New Dispatch (911)</strong> to log a call.</div>
      </div>

      <!-- ============ REPORTS ============ -->
      <div v-if="tab === 'reports'" class="hms-card">
        <div class="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <label class="hms-label m-0">Window:</label>
          <select class="hms-input hms-filter" v-model.number="reportDays" @change="loadReports">
            <option :value="7">7 days</option>
            <option :value="30">30 days</option>
            <option :value="90">90 days</option>
            <option :value="365">1 year</option>
          </select>
          <span class="hms-muted ms-auto" v-if="reports">Window: {{ reports.window_days }} days</span>
        </div>
        <div v-if="reports && reports.summary">
          <div class="hms-stat-row">
            <div class="hms-stat"><div class="hms-stat-num">{{ reports.summary.total_dispatches || 0 }}</div><div class="hms-stat-lbl">Dispatches</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-green">{{ reports.summary.completed || 0 }}</div><div class="hms-stat-lbl">Completed</div></div>
            <div class="hms-stat"><div class="hms-stat-num hms-stat-amber">{{ reports.summary.cancelled || 0 }}</div><div class="hms-stat-lbl">Cancelled</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ reports.summary.emergency_count || 0 }}</div><div class="hms-stat-lbl">Emergency calls</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ fmtSec(reports.summary.avg_dispatch_sec) }}</div><div class="hms-stat-lbl">Avg call → dispatch</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ fmtSec(reports.summary.avg_to_scene_sec) }}</div><div class="hms-stat-lbl">Avg dispatch → on-scene</div></div>
            <div class="hms-stat"><div class="hms-stat-num">{{ fmtSec(reports.summary.avg_scene_to_dest_sec) }}</div><div class="hms-stat-lbl">Avg scene → destination</div></div>
          </div>
          <div class="row g-3 mt-2">
            <div class="col-lg-6">
              <div class="hms-section-label">PER-VEHICLE UTILISATION</div>
              <table class="hms-table">
                <thead><tr><th>Vehicle</th><th>Trips</th><th>Completed</th><th>Avg → scene</th></tr></thead>
                <tbody>
                  <tr v-for="r in reports.per_vehicle" :key="r.vehicle_id">
                    <td>{{ r.call_sign || r.registration_no }}</td>
                    <td>{{ r.trips }}</td>
                    <td>{{ r.completed || 0 }}</td>
                    <td>{{ fmtSec(r.avg_to_scene_sec) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-lg-6">
              <div class="hms-section-label">OUTCOMES</div>
              <table class="hms-table">
                <thead><tr><th>Outcome</th><th>#</th></tr></thead>
                <tbody>
                  <tr v-for="o in reports.outcomes" :key="o.outcome">
                    <td>{{ formatOutcome(o.outcome) }}</td>
                    <td>{{ o.n }}</td>
                  </tr>
                  <tr v-if="!reports.outcomes.length"><td colspan="2" class="hms-muted-sm">No outcomes recorded.</td></tr>
                </tbody>
              </table>
              <div class="hms-section-label mt-3">TOP COMPLAINTS</div>
              <table class="hms-table">
                <thead><tr><th>Complaint</th><th>#</th></tr></thead>
                <tbody>
                  <tr v-for="c in reports.top_complaints" :key="c.complaint"><td>{{ c.complaint }}</td><td>{{ c.n }}</td></tr>
                  <tr v-if="!reports.top_complaints.length"><td colspan="2" class="hms-muted-sm">None.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div v-else class="hms-spinner py-3"><div class="spinner-border"></div></div>
      </div>

      <!-- ============ NEW DISPATCH MODAL ============ -->
      <div class="hms-modal-overlay" v-if="dispatchModal" @click.self="dispatchModal=false">
        <div class="hms-modal" style="max-width:720px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-phone-in-talk hms-text-danger"></i> New Dispatch — Call Intake</span>
            <button class="hms-modal-close" @click="dispatchModal=false">&times;</button>
          </div>
          <div v-if="dispatchError" class="hms-alert hms-alert-danger">{{ dispatchError }}</div>

          <div class="hms-section-label">CALLER</div>
          <div class="row g-3 mb-3">
            <div class="col-md-5"><label class="hms-label">Caller name</label>
              <input class="hms-input" v-model="dispatchForm.caller_name" />
            </div>
            <div class="col-md-4"><label class="hms-label">Caller phone *</label>
              <input class="hms-input" v-model="dispatchForm.caller_phone" placeholder="07XX XXX XXX" />
            </div>
            <div class="col-md-3"><label class="hms-label">Priority</label>
              <select class="hms-input" v-model="dispatchForm.priority">
                <option value="emergency">Emergency</option>
                <option value="urgent">Urgent</option>
                <option value="routine">Routine</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">What is the emergency?</label>
              <textarea class="hms-input" rows="2" v-model="dispatchForm.complaint" placeholder="Chief complaint / what's wrong"></textarea>
            </div>
          </div>

          <div class="hms-section-label">PICKUP</div>
          <div class="row g-3 mb-3">
            <div class="col-12">
              <label class="hms-label">Search location</label>
              <div class="d-flex gap-2 flex-wrap">
                <input class="hms-input" style="flex:1; min-width:240px;" v-model="pickupQuery"
                       @keydown.enter.prevent="geocodePickup(false)"
                       @input="onPickupInput"
                       placeholder="Town, landmark, facility name…" />
                <button type="button" class="hms-btn hms-btn-blue" @click="geocodePickup(false)" :disabled="pickupBusy">
                  <i class="mdi" :class="pickupBusy ? 'mdi-loading mdi-spin' : 'mdi-magnify'"></i> Search
                </button>
                <button type="button" class="hms-btn hms-btn-blue" @click="usePickupGps" :disabled="pickupBusy" title="Use current GPS">
                  <i class="mdi mdi-crosshairs-gps"></i> GPS
                </button>
              </div>
              <div v-if="pickupSuggest.length" class="hms-suggest mt-1">
                <div v-for="(s, i) in pickupSuggest" :key="i" class="hms-suggest-item" @click="pickPickupSuggest(s)">
                  <i class="mdi mdi-map-marker-outline me-1"></i>{{ s.display_name }}
                </div>
              </div>
              <div ref="pickupMap" class="hms-leaflet-map mt-2"></div>
              <div class="hms-muted-sm mt-1">
                <span v-if="dispatchForm.pickup_lat != null && dispatchForm.pickup_lng != null">
                  <i class="mdi mdi-crosshairs-gps me-1"></i>
                  Pin: {{ Number(dispatchForm.pickup_lat).toFixed(5) }}, {{ Number(dispatchForm.pickup_lng).toFixed(5) }}
                </span>
                <span v-else>Click on the map or use GPS to drop a pickup pin.</span>
              </div>
              <input class="hms-input mt-2" v-model="dispatchForm.pickup_location_name" placeholder="Pickup location name (e.g. Mai Mahiu junction)" />
              <div class="hms-muted-sm mt-1">Caller will also get an SMS with a GPS-drop link so they can share their exact location.</div>
            </div>
          </div>

          <div class="hms-section-label">PATIENT (optional)</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Patient name (free text)</label>
              <input class="hms-input" v-model="dispatchForm.patient_name_freetext" />
            </div>
            <div class="col-md-3"><label class="hms-label">Age</label>
              <input type="number" class="hms-input" v-model.number="dispatchForm.patient_age" min="0" max="130" />
            </div>
            <div class="col-md-3"><label class="hms-label">Gender</label>
              <select class="hms-input" v-model="dispatchForm.patient_gender">
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div class="hms-section-label">INTER-FACILITY (optional)</div>
          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Source facility</label>
              <input class="hms-input" list="hms-facilities" v-model="dispatchForm.source_facility" placeholder="Type to search — or enter a new facility" />
              <div class="hms-muted-sm mt-1">Pick from the list, or type the name if it's not there yet.</div>
            </div>
            <div class="col-md-6"><label class="hms-label">Destination facility</label>
              <input class="hms-input" list="hms-facilities" v-model="dispatchForm.dest_facility" placeholder="Type to search — or enter a new facility" />
            </div>
            <datalist id="hms-facilities">
              <option v-for="f in facilityList" :key="f" :value="f"></option>
            </datalist>
            <div class="col-md-6"><label class="hms-label">Receiving doctor name</label>
              <input class="hms-input" v-model="dispatchForm.dest_doctor_name" />
            </div>
            <div class="col-md-6"><label class="hms-label">Receiving doctor phone</label>
              <input class="hms-input" v-model="dispatchForm.dest_doctor_phone" placeholder="07XX XXX XXX" />
              <div class="hms-muted-sm mt-1">Receiving doctor gets an SMS alert with patient brief + ETA on dispatch.</div>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-secondary flex-fill" @click="dispatchModal=false">Cancel</button>
            <button class="hms-btn hms-btn-red flex-fill" :disabled="saving" @click="submitDispatch">
              <i class="mdi" :class="saving ? 'mdi-loading mdi-spin' : 'mdi-phone-plus'"></i>
              {{ saving ? ' Saving…' : ' Log call' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ============ ASSIGN VEHICLE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="assignModal && assignTarget" @click.self="assignModal=false">
        <div class="hms-modal" style="max-width:520px;">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-truck-fast"></i> Assign vehicle &amp; crew</span>
            <button class="hms-modal-close" @click="assignModal=false">&times;</button>
          </div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Dispatch</span><span>{{ assignTarget.dispatch_no }} · {{ assignTarget.priority }}</span></div>
            <div><span class="hms-kv-key">Complaint</span><span>{{ assignTarget.complaint }}</span></div>
          </div>
          <div class="row g-3 mb-3">
            <div class="col-12"><label class="hms-label">Vehicle *</label>
              <select class="hms-input" v-model="assignForm.vehicle_id">
                <option value="">Select…</option>
                <option v-for="v in availableVehicles" :key="v.vehicle_id" :value="v.vehicle_id">
                  {{ v.call_sign || v.registration_no }} ({{ v.vehicle_type }}) · {{ formatStatus(v.status) }}
                </option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Driver</label>
              <select class="hms-input" v-model="assignForm.driver_crew_id">
                <option value="">—</option>
                <option v-for="c in crew.filter(c => c.role === 'driver')" :key="c.crew_id" :value="c.crew_id">{{ c.full_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Paramedic / Clinician</label>
              <select class="hms-input" v-model="assignForm.paramedic_crew_id">
                <option value="">—</option>
                <option v-for="c in crew.filter(c => c.role !== 'driver')" :key="c.crew_id" :value="c.crew_id">{{ c.full_name }} ({{ c.role }})</option>
              </select>
            </div>
          </div>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-secondary flex-fill" @click="assignModal=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" :disabled="saving || !assignForm.vehicle_id" @click="submitAssign">
              <i class="mdi" :class="saving ? 'mdi-loading mdi-spin' : 'mdi-check'"></i> Dispatch
            </button>
          </div>
        </div>
      </div>

      <!-- ============ VEHICLE MODAL ============ -->
      <div class="hms-modal-overlay" v-if="vehicleModal" @click.self="vehicleModal=false">
        <div class="hms-modal" style="max-width:720px;">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="vehicleForm.vehicle_id ? 'mdi-pencil' : 'mdi-truck-plus'"></i>
              {{ vehicleForm.vehicle_id ? 'Edit Ambulance' : 'Add Ambulance' }}</span>
            <button class="hms-modal-close" @click="vehicleModal=false">&times;</button>
          </div>
          <div v-if="vehicleError" class="hms-alert hms-alert-danger">{{ vehicleError }}</div>

          <div class="hms-section-label">IDENTITY</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Registration No *</label>
              <input class="hms-input" v-model="vehicleForm.registration_no" placeholder="KCA 123A" />
            </div>
            <div class="col-md-4"><label class="hms-label">Call sign</label>
              <input class="hms-input" v-model="vehicleForm.call_sign" placeholder="NAROK-1" />
            </div>
            <div class="col-md-4"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="vehicleForm.vehicle_type">
                <option value="BLS">BLS (Basic Life Support)</option>
                <option value="ALS">ALS (Advanced Life Support)</option>
                <option value="PICU">PICU</option>
                <option value="NICU">NICU</option>
                <option value="transport_van">Transport van</option>
                <option value="4x4">4x4</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="boda">Boda boda (motorcycle ambulance)</option>
                <option value="bicycle">Bicycle ambulance</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">Make</label>
              <input class="hms-input" v-model="vehicleForm.make" placeholder="Toyota" />
            </div>
            <div class="col-md-3"><label class="hms-label">Model</label>
              <input class="hms-input" v-model="vehicleForm.model" placeholder="Hiace" />
            </div>
            <div class="col-md-2"><label class="hms-label">Year</label>
              <input type="number" class="hms-input" v-model.number="vehicleForm.year_of_manufacture" min="1980" max="2030" />
            </div>
            <div class="col-md-2"><label class="hms-label">Stretchers</label>
              <input type="number" class="hms-input" v-model.number="vehicleForm.capacity_stretchers" min="0" max="10" />
            </div>
            <div class="col-md-2"><label class="hms-label">Seated</label>
              <input type="number" class="hms-input" v-model.number="vehicleForm.capacity_seated" min="0" max="20" />
            </div>
          </div>

          <div class="hms-section-label">STATUS &amp; BASE</div>
          <div class="row g-3 mb-3">
            <div class="col-md-4"><label class="hms-label">Status</label>
              <select class="hms-input" v-model="vehicleForm.status">
                <option value="available">Available</option>
                <option value="on_call">On call</option>
                <option value="en_route">En route</option>
                <option value="on_scene">On scene</option>
                <option value="transporting">Transporting</option>
                <option value="returning">Returning</option>
                <option value="out_of_service">Out of service</option>
                <option value="maintenance">Maintenance</option>
                <option value="offline">Offline</option>
              </select>
            </div>
            <div class="col-md-8"><label class="hms-label">Base location</label>
              <input class="hms-input" v-model="vehicleForm.base_location_name" placeholder="Narok County Hospital ER" />
            </div>
            <div class="col-md-3"><label class="hms-label">Fuel %</label>
              <input type="number" class="hms-input" v-model.number="vehicleForm.fuel_level_pct" min="0" max="100" />
            </div>
            <div class="col-md-3"><label class="hms-label">Odometer (km)</label>
              <input type="number" class="hms-input" v-model.number="vehicleForm.odometer_km" />
            </div>
            <div class="col-md-3"><label class="hms-label">Insurance expiry</label>
              <input type="date" class="hms-input" v-model="vehicleForm.insurance_expiry" />
            </div>
            <div class="col-md-3"><label class="hms-label">License expiry</label>
              <input type="date" class="hms-input" v-model="vehicleForm.license_expiry" />
            </div>
            <div class="col-md-3"><label class="hms-label">Last service</label>
              <input type="date" class="hms-input" v-model="vehicleForm.last_service_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Next service due</label>
              <input type="date" class="hms-input" v-model="vehicleForm.next_service_due" />
            </div>
            <div class="col-md-6"><label class="hms-check mt-4">
              <input type="checkbox" v-model="vehicleForm.is_active" :true-value="1" :false-value="0" /> Active in fleet
            </label></div>
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="vehicleForm.notes"></textarea>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-secondary flex-fill" @click="vehicleModal=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" :disabled="saving" @click="saveVehicle">
              <i class="mdi" :class="saving ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
              {{ saving ? ' Saving…' : ' Save' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ============ CREW MODAL ============ -->
      <div class="hms-modal-overlay" v-if="crewModal" @click.self="crewModal=false">
        <div class="hms-modal" style="max-width:640px;">
          <div class="hms-modal-title">
            <span><i class="mdi" :class="crewForm.crew_id ? 'mdi-pencil' : 'mdi-account-plus'"></i>
              {{ crewForm.crew_id ? 'Edit Crew' : 'Add Crew' }}</span>
            <button class="hms-modal-close" @click="crewModal=false">&times;</button>
          </div>
          <div v-if="crewError" class="hms-alert hms-alert-danger">{{ crewError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-7"><label class="hms-label">Full Name *</label>
              <input class="hms-input" v-model="crewForm.full_name" />
            </div>
            <div class="col-md-5"><label class="hms-label">Role</label>
              <select class="hms-input" v-model="crewForm.role">
                <option value="driver">Driver</option>
                <option value="paramedic">Paramedic</option>
                <option value="emt">EMT</option>
                <option value="nurse">Nurse</option>
                <option value="doctor">Doctor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Phone</label>
              <input class="hms-input" v-model="crewForm.phone" />
            </div>
            <div class="col-md-6"><label class="hms-label">Email</label>
              <input class="hms-input" type="email" v-model="crewForm.email" />
            </div>
            <div class="col-md-6"><label class="hms-label">Cert / Licence No</label>
              <input class="hms-input" v-model="crewForm.cert_number" placeholder="KMPDC / NCK / DL no" />
            </div>
            <div class="col-md-3"><label class="hms-label">Cert expiry</label>
              <input type="date" class="hms-input" v-model="crewForm.cert_expiry_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Blood group</label>
              <select class="hms-input" v-model="crewForm.blood_group">
                <option value="">—</option>
                <option v-for="bg in ['A+','A-','B+','B-','AB+','AB-','O+','O-']" :key="bg">{{ bg }}</option>
              </select>
            </div>
            <div class="col-md-3"><label class="hms-label">BLS expiry</label>
              <input type="date" class="hms-input" v-model="crewForm.bls_expiry_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">ACLS expiry</label>
              <input type="date" class="hms-input" v-model="crewForm.acls_expiry_date" />
            </div>
            <div class="col-md-3"><label class="hms-label">Driver's licence no</label>
              <input class="hms-input" v-model="crewForm.drivers_license_no" />
            </div>
            <div class="col-md-3"><label class="hms-label">DL expiry</label>
              <input type="date" class="hms-input" v-model="crewForm.drivers_license_expiry" />
            </div>
            <div class="col-12"><label class="hms-label">Notes</label>
              <textarea class="hms-input" rows="2" v-model="crewForm.notes"></textarea>
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-secondary flex-fill" @click="crewModal=false">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" :disabled="saving" @click="saveCrew">
              <i class="mdi" :class="saving ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
              {{ saving ? ' Saving…' : ' Save' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "AmbulanceManagement",
  components: { Navigation },
  data() {
    return {
      loading: true,
      tab: "map",

      vehicles: [],
      crew: [],
      liveLocations: [],
      liveTimer: null,

      vehicleModal: false,
      vehicleForm: this.emptyVehicle(),
      vehicleError: "",

      crewModal: false,
      crewForm: this.emptyCrew(),
      crewError: "",

      saving: false,
      toastMsg: "", toastError: false, toastTimer: null,
      _map: null,
      _markers: {},    // vehicle_id → Leaflet marker

      // Dispatch
      dispatches: [],
      dispatchStatusFilter: 'active',
      dispatchModal: false,
      dispatchForm: this.emptyDispatch(),
      dispatchError: '',
      dispatchStates: ['received','dispatched','en_route','on_scene','loading','transporting','at_destination','returned','cancelled'],
      assignModal: false,
      assignTarget: null,
      assignForm: { vehicle_id: '', driver_crew_id: '', paramedic_crew_id: '' },

      // Reports
      reports: null,
      reportDays: 30,

      // Pickup map (dispatch modal)
      pickupQuery: '', pickupSuggest: [], pickupBusy: false, pickupDebounce: null,
      _pickupMap: null, _pickupMarker: null,

      // Common Kenyan facilities (curated, fall-back when no facility table exists yet)
      facilityList: [
        // Narok + neighbouring satellites
        'Narok County Referral Hospital',
        'Kilgoris Sub-County Hospital',
        'Loita Sub-County Hospital',
        'Lolgorian Sub-County Hospital',
        'Ololulunga Sub-County Hospital',
        'Suswa Sub-County Hospital',
        'Talek Health Centre',
        'Nairagie Enkare Health Centre',
        'Olokurto Health Centre',
        'Ewuaso-Kedong Dispensary',
        'Mai Mahiu Sub-District Hospital',
        // National + Nairobi referrals
        'Kenyatta National Hospital',
        'Moi Teaching and Referral Hospital',
        'Mater Misericordiae Hospital',
        'Aga Khan University Hospital',
        'Nairobi Hospital',
        'Karen Hospital',
        'MP Shah Hospital',
        'Avenue Hospital',
        'Gertrude\'s Children\'s Hospital',
        'Kenyatta University Teaching, Referral & Research Hospital',
        // County referrals (Rift Valley)
        'Nakuru County Referral Hospital',
        'Eldoret Sub-County Hospital',
        'Kakamega County Referral Hospital',
        'Kisumu County Referral Hospital',
        'Bomet County Referral Hospital',
        'Kajiado County Referral Hospital',
        // Private chain
        'Equity Afia',
        'AAR Healthcare',
        'Bliss Healthcare',
        'Penda Health',
        'CFM Health'
      ]
    };
  },
  computed: {
    enRouteCount() {
      var s = ['on_call','en_route','on_scene','transporting','returning'];
      return this.vehicles.filter(v => s.indexOf(v.status) >= 0).length;
    },
    activeDispatchCount() {
      return this.dispatches.filter(d => ['received','dispatched','en_route','on_scene','loading','transporting'].includes(d.status)).length;
    },
    availableVehicles() {
      return this.vehicles.filter(v => v.is_active == 1 && ['available','on_call','returning','offline'].indexOf(v.status) >= 0);
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.loadAll();
  },
  beforeUnmount() {
    if (this.liveTimer) clearInterval(this.liveTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.destroyMap();
  },
  watch: {
    tab(v) {
      if (v === 'map') { this.$nextTick(() => this.initMap()); this.startLivePolling(); }
      else { this.stopLivePolling(); }
    },
    dispatchModal(v) {
      if (v) { this.$nextTick(() => this.initPickupMap()); }
      else  { this.destroyPickupMap(); this.pickupSuggest = []; }
    }
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(m, err) {
      this.toastMsg = m; this.toastError = !!err;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastMsg = ''; }, 2800);
    },
    emptyVehicle() {
      return {
        vehicle_id: null, registration_no: '', call_sign: '', make: '', model: '', year_of_manufacture: null,
        color: '', vehicle_type: 'BLS', capacity_stretchers: 1, capacity_seated: 4, status: 'offline',
        base_location_name: '', fuel_level_pct: null, odometer_km: null,
        last_service_date: '', next_service_due: '', insurance_expiry: '', license_expiry: '',
        notes: '', is_active: 1
      };
    },
    emptyCrew() {
      return {
        crew_id: null, full_name: '', phone: '', email: '', role: 'paramedic',
        cert_number: '', cert_expiry_date: '', bls_expiry_date: '', acls_expiry_date: '',
        drivers_license_no: '', drivers_license_expiry: '', blood_group: '', notes: '', is_active: 1
      };
    },
    emptyDispatch() {
      return {
        caller_name: '', caller_phone: '', caller_relationship: '', priority: 'urgent',
        complaint: '', pickup_location_name: '',
        patient_id: null, patient_name_freetext: '', patient_age: null, patient_gender: '',
        source_facility: '', source_facility_type: 'unknown',
        dest_facility: '', dest_facility_type: 'unknown',
        dest_doctor_name: '', dest_doctor_phone: ''
      };
    },

    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([this.loadVehicles(), this.loadCrew()]);
        if (this.tab === 'map') await this.loadLive();
      } catch(e) {}
      this.loading = false;
      if (this.tab === 'map') {
        this.$nextTick(() => { this.initMap(); this.startLivePolling(); });
      }
    },
    async loadVehicles() {
      var r = await executeJson({ function: 'getAmbulances' }, hms);
      if (r.data && r.data.success) this.vehicles = (r.data.data.rows) || [];
    },
    async loadCrew() {
      var r = await executeJson({ function: 'getAmbulanceCrews' }, hms);
      if (r.data && r.data.success) this.crew = (r.data.data.rows) || [];
    },
    async loadLive() {
      try {
        var r = await executeJson({ function: 'getAmbulanceLiveLocations' }, hms);
        if (r.data && r.data.success) {
          this.liveLocations = (r.data.data.rows) || [];
          this.drawMarkers();
        }
      } catch(e) {}
    },
    startLivePolling() {
      this.stopLivePolling();
      this.liveTimer = setInterval(() => { if (this.tab === 'map') this.loadLive(); }, 30000);
    },
    stopLivePolling() { if (this.liveTimer) { clearInterval(this.liveTimer); this.liveTimer = null; } },

    // ===== Leaflet =====
    initMap() {
      if (typeof window === 'undefined' || typeof window.L === 'undefined') {
        setTimeout(() => { if (this.tab === 'map') this.initMap(); }, 400); return;
      }
      var el = this.$refs.liveMap;
      if (!el || this._map) { if (this._map) { try { this._map.invalidateSize(); } catch(e) {} } return; }
      var L = window.L;
      this._map = L.map(el).setView([-1.0833, 35.8667], 9);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(this._map);
      this.drawMarkers();
    },
    destroyMap() {
      if (this._map) { try { this._map.remove(); } catch(e) {} this._map = null; this._markers = {}; }
    },
    drawMarkers() {
      if (!this._map || typeof window.L === 'undefined') return;
      var L = window.L;
      var keep = {};
      var bounds = [];
      this.liveLocations.forEach(v => {
        if (v.latitude == null || v.longitude == null) return;
        var lat = Number(v.latitude); var lng = Number(v.longitude);
        keep[v.vehicle_id] = true;
        bounds.push([lat, lng]);
        var color = this.markerColor(v.status, v.seconds_since_ping);
        var html = '<div class="hms-mk" style="background:' + color + '">🚑<br><b>' + (v.call_sign || v.registration_no) + '</b></div>';
        var icon = L.divIcon({ html: html, className: 'hms-mk-wrap', iconSize: [80, 50], iconAnchor: [40, 50] });
        if (this._markers[v.vehicle_id]) {
          this._markers[v.vehicle_id].setLatLng([lat, lng]).setIcon(icon);
        } else {
          this._markers[v.vehicle_id] = L.marker([lat, lng], { icon: icon }).addTo(this._map);
        }
        this._markers[v.vehicle_id].bindTooltip(
          (v.call_sign || v.registration_no) + ' · ' + this.formatStatus(v.status)
          + (v.speed_kmh != null ? ' · ' + Math.round(v.speed_kmh) + ' km/h' : '')
          + (v.last_ping_at ? ' · ' + this.ago(v.seconds_since_ping) : '')
        );
      });
      // Remove markers for vehicles no longer in the set
      Object.keys(this._markers).forEach(k => {
        if (!keep[k]) { try { this._map.removeLayer(this._markers[k]); } catch(e) {} delete this._markers[k]; }
      });
      // Auto-fit bounds the first time we have data
      if (bounds.length && !this._didFit) {
        try { this._map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 }); this._didFit = true; } catch(e) {}
      }
    },
    markerColor(status, secs) {
      if (secs == null || secs > 180) return '#777';
      if (status === 'available') return '#28a745';
      if (status === 'maintenance' || status === 'out_of_service') return '#dc3545';
      if (status === 'offline') return '#777';
      return '#0047AB';
    },
    focusVehicle(v) {
      if (!this._map || v.latitude == null) return;
      this._map.setView([Number(v.latitude), Number(v.longitude)], 15);
      if (this._markers[v.vehicle_id]) this._markers[v.vehicle_id].openTooltip();
    },

    // ===== Vehicle CRUD =====
    openVehicleForm(v) {
      this.vehicleError = '';
      this.vehicleForm = v ? Object.assign(this.emptyVehicle(), {
        vehicle_id:        v.vehicle_id,
        registration_no:   v.registration_no   || '',
        call_sign:         v.call_sign         || '',
        make:              v.make              || '',
        model:             v.model             || '',
        year_of_manufacture: v.year_of_manufacture || null,
        color:             v.color             || '',
        vehicle_type:      v.vehicle_type      || 'BLS',
        capacity_stretchers: Number(v.capacity_stretchers || 1),
        capacity_seated:     Number(v.capacity_seated || 4),
        status:            v.status            || 'offline',
        base_location_name:v.base_location_name|| '',
        fuel_level_pct:    v.fuel_level_pct    != null ? Number(v.fuel_level_pct) : null,
        odometer_km:       v.odometer_km       != null ? Number(v.odometer_km)    : null,
        last_service_date: v.last_service_date ? String(v.last_service_date).slice(0,10) : '',
        next_service_due:  v.next_service_due  ? String(v.next_service_due).slice(0,10)  : '',
        insurance_expiry:  v.insurance_expiry  ? String(v.insurance_expiry).slice(0,10)  : '',
        license_expiry:    v.license_expiry    ? String(v.license_expiry).slice(0,10)    : '',
        notes:             v.notes             || '',
        is_active:         Number(v.is_active != null ? v.is_active : 1)
      }) : this.emptyVehicle();
      this.vehicleModal = true;
    },
    async saveVehicle() {
      this.vehicleError = '';
      if (!this.vehicleForm.registration_no) { this.vehicleError = 'Registration number is required.'; return; }
      this.saving = true;
      try {
        var fn = this.vehicleForm.vehicle_id ? 'updateAmbulance' : 'createAmbulance';
        var r = await executeJson(Object.assign({ function: fn }, this.vehicleForm), hms);
        if (r.data && r.data.success) {
          this.showToast(r.data.message || 'Saved.');
          this.vehicleModal = false;
          await this.loadVehicles();
        } else { this.vehicleError = (r.data && r.data.message) || 'Failed.'; }
      } catch(e) { this.vehicleError = 'Connection error.'; }
      this.saving = false;
    },
    async cycleStatus(v) {
      var order = ['available','on_call','en_route','on_scene','transporting','returning','out_of_service','maintenance','offline'];
      var i = order.indexOf(v.status);
      var next = order[(i + 1) % order.length];
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: 'updateAmbulance' }, v, { status: next }), hms);
        if (r.data && r.data.success) { this.showToast('Status: ' + this.formatStatus(next)); await this.loadVehicles(); }
      } catch(e) {}
      this.saving = false;
    },
    async confirmDeleteVehicle(v) {
      if (!confirm('Remove ' + (v.call_sign || v.registration_no) + ' from the fleet?')) return;
      try {
        var r = await executeJson({ function: 'deleteAmbulance', vehicle_id: v.vehicle_id }, hms);
        if (r.data && r.data.success) { this.showToast('Removed.'); await this.loadVehicles(); }
      } catch(e) {}
    },

    // ===== Crew CRUD =====
    openCrewForm(c) {
      this.crewError = '';
      this.crewForm = c ? Object.assign(this.emptyCrew(), {
        crew_id: c.crew_id, full_name: c.full_name, phone: c.phone || '', email: c.email || '', role: c.role || 'paramedic',
        cert_number: c.cert_number || '', cert_expiry_date: c.cert_expiry_date ? String(c.cert_expiry_date).slice(0,10) : '',
        bls_expiry_date:  c.bls_expiry_date  ? String(c.bls_expiry_date).slice(0,10)  : '',
        acls_expiry_date: c.acls_expiry_date ? String(c.acls_expiry_date).slice(0,10) : '',
        drivers_license_no:     c.drivers_license_no || '',
        drivers_license_expiry: c.drivers_license_expiry ? String(c.drivers_license_expiry).slice(0,10) : '',
        blood_group: c.blood_group || '', notes: c.notes || '', is_active: Number(c.is_active != null ? c.is_active : 1)
      }) : this.emptyCrew();
      this.crewModal = true;
    },
    async saveCrew() {
      this.crewError = '';
      if (!this.crewForm.full_name) { this.crewError = 'Name is required.'; return; }
      this.saving = true;
      try {
        var fn = this.crewForm.crew_id ? 'updateAmbulanceCrew' : 'createAmbulanceCrew';
        var r = await executeJson(Object.assign({ function: fn }, this.crewForm), hms);
        if (r.data && r.data.success) {
          this.showToast(r.data.message || 'Saved.');
          this.crewModal = false;
          await this.loadCrew();
        } else { this.crewError = (r.data && r.data.message) || 'Failed.'; }
      } catch(e) { this.crewError = 'Connection error.'; }
      this.saving = false;
    },
    async confirmDeleteCrew(c) {
      if (!confirm('Remove ' + c.full_name + ' from the crew?')) return;
      try {
        var r = await executeJson({ function: 'deleteAmbulanceCrew', crew_id: c.crew_id }, hms);
        if (r.data && r.data.success) { this.showToast('Removed.'); await this.loadCrew(); }
      } catch(e) {}
    },
    async toggleDuty(c) {
      var newDuty = c.on_duty == 1 ? 0 : 1;
      var vehicleId = c.assigned_vehicle_id || null;
      if (newDuty === 1 && !vehicleId) {
        var pick = prompt('Assign to which vehicle? Enter call sign or registration (leave blank for none):');
        if (pick) {
          var match = this.vehicles.find(v => (v.call_sign || '').toLowerCase() === pick.toLowerCase() || v.registration_no.toLowerCase() === pick.toLowerCase());
          if (match) vehicleId = match.vehicle_id;
        }
      }
      try {
        var r = await executeJson({ function: 'setCrewDuty', crew_id: c.crew_id, on_duty: newDuty, assigned_vehicle_id: vehicleId || '' }, hms);
        if (r.data && r.data.success) { this.showToast(r.data.message || 'Updated.'); await this.loadCrew(); }
      } catch(e) {}
    },

    // ===== Misc =====
    copy(text) {
      try { navigator.clipboard.writeText(text); this.showToast('Copied to clipboard.'); }
      catch(e) { this.showToast('Could not copy.', true); }
    },
    async smsTracker(v) {
      var phone = prompt('Driver phone (07XX XXX XXX):');
      if (!phone) return;
      var msg = 'Open this URL on your phone to start tracking ambulance ' + (v.call_sign || v.registration_no) + ': ' + v.tracker_url;
      try {
        var r = await executeJson({ function: 'testNotificationSms', phone: phone, message: msg }, hms);
        this.showToast((r.data && r.data.message) || 'Sent.', !(r.data && r.data.success));
      } catch(e) { this.showToast('Connection error.', true); }
    },
    // ===== DISPATCH =====
    async loadDispatches() {
      try {
        var r = await executeJson({ function: 'getDispatches', status: this.dispatchStatusFilter }, hms);
        if (r.data && r.data.success) this.dispatches = (r.data.data.rows) || [];
      } catch(e) {}
    },
    openDispatchForm() {
      this.dispatchError = '';
      this.dispatchForm = this.emptyDispatch();
      this.dispatchModal = true;
    },
    async submitDispatch() {
      this.dispatchError = '';
      if (!this.dispatchForm.caller_phone) { this.dispatchError = 'Caller phone is required.'; return; }
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: 'createDispatch' }, this.dispatchForm), hms);
        if (r.data && r.data.success) {
          this.showToast((r.data && r.data.message) || 'Dispatch created.');
          this.dispatchModal = false;
          await this.loadDispatches();
        } else { this.dispatchError = (r.data && r.data.message) || 'Failed.'; }
      } catch(e) { this.dispatchError = 'Connection error.'; }
      this.saving = false;
    },
    openAssign(d) {
      this.assignTarget = d;
      this.assignForm = { vehicle_id: '', driver_crew_id: '', paramedic_crew_id: '' };
      // Pre-pick first on-duty crew if available
      var onDuty = this.crew.filter(c => c.on_duty == 1);
      var drv = onDuty.find(c => c.role === 'driver');     if (drv) this.assignForm.driver_crew_id = drv.crew_id;
      var med = onDuty.find(c => c.role !== 'driver');     if (med) this.assignForm.paramedic_crew_id = med.crew_id;
      this.assignModal = true;
    },
    async submitAssign() {
      if (!this.assignTarget || !this.assignForm.vehicle_id) return;
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({
          function: 'assignDispatch',
          dispatch_id: this.assignTarget.dispatch_id
        }, this.assignForm), hms);
        if (r.data && r.data.success) {
          this.showToast((r.data && r.data.message) || 'Dispatched.');
          this.assignModal = false;
          await Promise.all([this.loadDispatches(), this.loadVehicles()]);
        } else { this.showToast((r.data && r.data.message) || 'Failed.', true); }
      } catch(e) { this.showToast('Connection error.', true); }
      this.saving = false;
    },
    async transition(d, toStatus) {
      if (toStatus === d.status) return;
      if (toStatus === 'cancelled') {
        var reason = prompt('Reason for cancelling?');
        if (reason == null) return;
      }
      try {
        var payload = { function: 'transitionDispatch', dispatch_id: d.dispatch_id, to_status: toStatus };
        if (reason) payload.cancel_reason = reason;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.showToast('Status: ' + this.formatDispatchStatus(toStatus));
          await Promise.all([this.loadDispatches(), this.loadVehicles()]);
        } else { this.showToast((r.data && r.data.message) || 'Failed.', true); }
      } catch(e) { this.showToast('Connection error.', true); }
    },
    priorityBadge(p) {
      var m = { emergency: 'hms-badge-danger', urgent: 'hms-badge-amber', routine: 'hms-badge-info' };
      return m[p] || 'hms-badge-neutral';
    },
    dispatchStatusBadge(s) {
      var m = { received: 'hms-badge-amber', dispatched: 'hms-badge-info', en_route: 'hms-badge-info', on_scene: 'hms-badge-info', loading: 'hms-badge-info', transporting: 'hms-badge-info', at_destination: 'hms-badge-info', returned: 'hms-badge-success', cancelled: 'hms-badge-neutral' };
      return m[s] || 'hms-badge-neutral';
    },
    formatDispatchStatus(s) {
      var m = { received:'Received', dispatched:'Dispatched', en_route:'En route', on_scene:'On scene', loading:'Loading', transporting:'Transporting', at_destination:'At destination', returned:'Returned', cancelled:'Cancelled' };
      return m[s] || s;
    },
    formatOutcome(o) {
      var m = { transported:'Transported', treated_released:'Treated & released', refused:'Refused care', no_patient_found:'No patient found', died_en_route:'Died en route', dead_on_arrival:'Dead on arrival', other:'Other' };
      return m[o] || o;
    },

    // ===== REPORTS =====
    async loadReports() {
      this.reports = null;
      try {
        var r = await executeJson({ function: 'getAmbulanceReports', days: this.reportDays }, hms);
        if (r.data && r.data.success) this.reports = r.data.data;
      } catch(e) {}
    },
    fmtSec(s) {
      if (s == null) return '—';
      var n = Math.round(Number(s));
      if (n < 60) return n + 's';
      if (n < 3600) return Math.floor(n/60) + 'm ' + (n%60) + 's';
      return Math.floor(n/3600) + 'h ' + Math.floor((n%3600)/60) + 'm';
    },

    // ===== PICKUP MAP (dispatch modal) =====
    initPickupMap() {
      if (typeof window === 'undefined' || typeof window.L === 'undefined') {
        setTimeout(() => { if (this.dispatchModal) this.initPickupMap(); }, 400); return;
      }
      var el = this.$refs.pickupMap;
      if (!el) return;
      if (this._pickupMap) { try { this._pickupMap.remove(); } catch(e) {} this._pickupMap = null; this._pickupMarker = null; }
      var hasPin = this.dispatchForm.pickup_lat != null && this.dispatchForm.pickup_lng != null;
      var startLat = hasPin ? Number(this.dispatchForm.pickup_lat) : -1.0833;
      var startLng = hasPin ? Number(this.dispatchForm.pickup_lng) :  35.8667;
      var L = window.L;
      this._pickupMap = L.map(el, { zoomControl: true }).setView([startLat, startLng], hasPin ? 16 : 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(this._pickupMap);
      this._pickupMarker = L.marker([startLat, startLng], { draggable: true }).addTo(this._pickupMap);
      var self = this;
      this._pickupMarker.on('dragend', function(e) {
        var ll = e.target.getLatLng();
        self.dispatchForm.pickup_lat = +ll.lat.toFixed(7);
        self.dispatchForm.pickup_lng = +ll.lng.toFixed(7);
      });
      this._pickupMap.on('click', function(e) {
        self._pickupMarker.setLatLng(e.latlng);
        self.dispatchForm.pickup_lat = +e.latlng.lat.toFixed(7);
        self.dispatchForm.pickup_lng = +e.latlng.lng.toFixed(7);
      });
      setTimeout(() => { try { this._pickupMap.invalidateSize(); } catch(e) {} }, 250);
    },
    destroyPickupMap() {
      if (this._pickupMap) { try { this._pickupMap.remove(); } catch(e) {} this._pickupMap = null; this._pickupMarker = null; }
    },
    setPickupPin(lat, lng, zoom) {
      if (!this._pickupMap) return;
      this._pickupMap.setView([lat, lng], zoom || 16);
      if (this._pickupMarker) this._pickupMarker.setLatLng([lat, lng]);
      else this._pickupMarker = window.L.marker([lat, lng], { draggable: true }).addTo(this._pickupMap);
      this.dispatchForm.pickup_lat = +Number(lat).toFixed(7);
      this.dispatchForm.pickup_lng = +Number(lng).toFixed(7);
    },
    onPickupInput() {
      if (this.pickupDebounce) clearTimeout(this.pickupDebounce);
      if (!this.pickupQuery || this.pickupQuery.length < 3) { this.pickupSuggest = []; return; }
      this.pickupDebounce = setTimeout(() => { this.geocodePickup(true); }, 500);
    },
    async geocodePickup(suggestOnly) {
      var q = (this.pickupQuery || '').trim();
      if (!q) { this.pickupSuggest = []; return; }
      this.pickupBusy = !suggestOnly;
      try {
        var url = 'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&countrycodes=ke&q=' + encodeURIComponent(q);
        var resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
        var data = await resp.json();
        if (Array.isArray(data) && data.length) {
          if (suggestOnly) this.pickupSuggest = data;
          else this.pickPickupSuggest(data[0]);
        } else if (!suggestOnly) {
          this.showToast('No matches — drop a pin on the map.', true);
        }
      } catch (e) {
        if (!suggestOnly) this.showToast('Geocoder unreachable — drop a pin manually.', true);
      }
      this.pickupBusy = false;
    },
    pickPickupSuggest(s) {
      this.pickupSuggest = [];
      this.pickupQuery = s.display_name;
      if (!this.dispatchForm.pickup_location_name) this.dispatchForm.pickup_location_name = s.display_name;
      this.setPickupPin(parseFloat(s.lat), parseFloat(s.lon), 16);
    },
    usePickupGps() {
      if (!navigator.geolocation) { this.showToast('Geolocation not supported.', true); return; }
      this.pickupBusy = true;
      navigator.geolocation.getCurrentPosition((pos) => {
        this.setPickupPin(pos.coords.latitude, pos.coords.longitude, 17);
        this.pickupBusy = false;
      }, (err) => { this.showToast('GPS error: ' + err.message, true); this.pickupBusy = false; },
      { enableHighAccuracy: true, timeout: 10000 });
    },

    statusCount(s) { return this.vehicles.filter(v => v.status === s).length; },
    statusBadge(s) {
      var m = { available:'hms-badge-success', on_call:'hms-badge-info', en_route:'hms-badge-info', on_scene:'hms-badge-info', transporting:'hms-badge-info', returning:'hms-badge-info', out_of_service:'hms-badge-amber', maintenance:'hms-badge-amber', offline:'hms-badge-neutral' };
      return m[s] || 'hms-badge-neutral';
    },
    formatStatus(s) {
      var m = { available:'Available', on_call:'On call', en_route:'En route', on_scene:'On scene', transporting:'Transporting', returning:'Returning', out_of_service:'Out of service', maintenance:'Maintenance', offline:'Offline' };
      return m[s] || s;
    },
    ago(secs) {
      if (secs == null) return '—';
      if (secs < 60)   return secs + 's ago';
      if (secs < 3600) return Math.floor(secs/60) + 'm ago';
      if (secs < 86400)return Math.floor(secs/3600) + 'h ago';
      return Math.floor(secs/86400) + 'd ago';
    },
    shortDateTime(dt) {
      if (!dt) return '—';
      try { var d = new Date(String(dt).replace(' ', 'T')); return d.toLocaleString(undefined, { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }); }
      catch(e) { return dt; }
    }
  }
};
</script>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
.hms-mk-wrap { background: transparent !important; border: none !important; }
.hms-mk { color:#fff; font-weight: 700; font-size: 11px; padding: 4px 8px; border-radius: 8px; text-align: center; box-shadow: 0 4px 10px rgba(0,0,0,.3); line-height: 1.2; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { font-size: 20px; font-weight: 700; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 10px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; flex: 1; min-width: 130px; }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-green { color: var(--hms-success); }
.hms-stat-blue { color: var(--hms-primary); }
.hms-stat-amber { color: #f59e0b; }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.hms-tab.active, .hms-tab:hover { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; margin-bottom: 14px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-live-map { width: 100%; height: 480px; border-radius: 10px; border: 1px solid var(--hms-br); overflow: hidden; }
.hms-vehicle-pill { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 4px solid #777; border-radius: 8px; padding: 8px 12px; cursor: pointer; transition: .15s; }
.hms-vehicle-pill:hover { background: var(--hms-primary-lt); }
.hms-vehicle-pill.st-available { border-left-color: var(--hms-success); }
.hms-vehicle-pill.st-en_route, .hms-vehicle-pill.st-on_scene, .hms-vehicle-pill.st-transporting, .hms-vehicle-pill.st-returning, .hms-vehicle-pill.st-on_call { border-left-color: var(--hms-primary); }
.hms-vehicle-pill.st-maintenance, .hms-vehicle-pill.st-out_of_service { border-left-color: #f59e0b; }
.hms-vehicle-pill.st-offline { border-left-color: #777; opacity: 0.7; }
.hms-vp-head { display: flex; justify-content: space-between; align-items: center; }
.hms-vp-sign { font-weight: 700; }

.hms-vehicle-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px; }
.hms-vc-sign { font-size: 18px; font-weight: 800; color: var(--hms-primary); }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: var(--hms-h); }
.hms-tracker-line { background: var(--hms-bg3); border: 1px dashed var(--hms-br); border-radius: 8px; padding: 8px 10px; margin: 6px 0; }
.hms-trk-url { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th, .hms-table td { padding: 8px 10px; border-bottom: 1px solid var(--hms-br); font-size: 13px; }
.hms-table th { color: var(--hms-s); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-badge { padding: 3px 10px; border-radius: 50px; font-size: 11px; font-weight: 600; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-info { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-amber { background: rgba(245,158,11,.15); color: #f59e0b; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-s); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 14px; font-size: 13px; font-weight: 600; cursor: pointer; transition: .15s; display: inline-flex; align-items: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-red { background: var(--hms-danger); color: #fff; }
.hms-btn-red:hover:not(:disabled) { filter: brightness(1.08); }
.hms-btn-secondary { background: var(--hms-bg3); color: var(--hms-h); border: 1px solid var(--hms-br); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 22px; width: 100%; }
.hms-modal-title { font-size: 17px; font-weight: 700; margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-close:hover { color: var(--hms-h); }

.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
.hms-check input { accent-color: var(--hms-primary); }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }

.hms-tab { position: relative; }
.hms-tab-pill { background: var(--hms-danger); color: #fff; border-radius: 50px; padding: 1px 8px; margin-left: 6px; font-size: 11px; font-weight: 800; }
.hms-text-danger { color: var(--hms-danger); }
.hms-badge-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-input-sm { padding: 4px 8px; font-size: 12px; }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-spinner { display: flex; justify-content: center; padding: 30px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 130px; font-weight: 600; }

.hms-dispatch-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-left: 4px solid #888; border-radius: 12px; padding: 14px; height: 100%; display: flex; flex-direction: column; gap: 8px; }
.hms-dispatch-card.pr-emergency { border-left-color: var(--hms-danger); background: linear-gradient(180deg, rgba(220,53,69,.04) 0%, var(--hms-bg1) 60%); }
.hms-dispatch-card.pr-urgent    { border-left-color: #f59e0b; }
.hms-dispatch-card.pr-routine   { border-left-color: var(--hms-primary); }
.hms-dc-head { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
.hms-dc-no   { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: var(--hms-primary); font-weight: 700; }
.hms-dc-body { flex: 1; }
.hms-dc-actions { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }

.hms-leaflet-map { width: 100%; height: 240px; border-radius: 10px; border: 1px solid var(--hms-br); overflow: hidden; }
.hms-suggest      { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 8px; max-height: 200px; overflow-y: auto; }
.hms-suggest-item { padding: 8px 12px; font-size: 13px; cursor: pointer; border-bottom: 1px solid var(--hms-br); color: var(--hms-h); }
.hms-suggest-item:last-child { border-bottom: none; }
.hms-suggest-item:hover { background: var(--hms-primary-lt); }
</style>
